import React from "react";
import { CgWorkAlt } from "react-icons/cg";
import { FaReact, FaDocker, FaAws } from "react-icons/fa";
import {
  SiExpo,
  SiFirebase,
  SiFramer,
  SiGit,
  SiGithub,
  SiHtml5,
  SiJavascript,
  SiMongodb,
  SiNextdotjs,
  SiPython,
  SiReact,
  SiTailwindcss,
  SiTypescript,
  SiUnrealengine,
  SiPytorch,
  SiTensorflow,
  SiPostgresql,
  SiKeras,
  SiCplusplus,
  SiDocker,
  SiKubernetes,
  SiOnnx,
  SiNvidia,
  SiOpencv,
} from "react-icons/si";
import { LuGraduationCap, LuBriefcase, LuBuilding2 } from "react-icons/lu";
import pidImg from "@/public/polyu-sm.png";
import caperImg from "@/public/instacart-logo.png";
import tiktokImg from "@/public/tiktok-clean.png";
import aifiImg from "@/public/aifi-clean.png";
import polyuImg from "@/public/polyu-sm.png";

const es = {
  links: [
    {
      name: "Inicio",
      hash: "#home",
    },
    {
      name: "Acerca",
      hash: "#about",
    },
    {
      name: "Proyectos",
      hash: "#projects",
    },
    {
      name: "Habilidades",
      hash: "#skills",
    },
    {
      name: "Experiencia",
      hash: "#experience",
    },
    {
      name: "LLMVisualizer",
      hash: "/llmpage",
    },
  ],
  intro: {
    greeting: "¡Hola! Soy Wangpeng An.",
    heading: "Ingeniero Staff de Machine Learning Full Stack",
    main: "Construyo sistemas multimodales LLM y VLM, del entrenamiento al despliegue. Llevo cerca de diez años en esto, y la mayor parte de mi tiempo va en hacer que la inferencia sea barata y fiable a escala, más que en el modelo en sí. He trabajado en cosas muy distintas, desde investigación en CVPR hasta sistemas que procesan cientos de millones de videos al día.",
    occupation:
      "Actualmente en Instacart (Caper Cart) | Antes en TikTok y Meta AI Research (FAIR) | Autor de CVPR Spotlight",
    contactButton: "Contáctame aquí",
    downloadCV: "Descargar CV",
    statusChip: "Construyendo sistemas VLM en Instacart",
  },
  about: {
    title: "Acerca de Mí",
    first:
      "Empecé en ingeniería de control, no en ML, y ese trasfondo todavía marca mi forma de pensar. Mi paper de CVPR Spotlight salió justo de ahí: si ves el entrenamiento de una red profunda como un problema de control con retroalimentación, puedes usar un controlador PID para guiar el optimizador (luego extendido a IEEE TNNLS, IF 11.368). Hice la maestría en Tsinghua trabajando en optimización de deep learning, estimación de pose y reconocimiento facial, con publicaciones en CVPR, IEEE TNNLS y Pattern Recognition, y una patente estadounidense de seguimiento para pago autónomo.",
    second:
      "Después de trabajar en Instacart, TikTok, Meta FAIR y AiFi, cada vez tengo más claro que lo que decide si un sistema de ML funciona en producción suele estar alrededor del modelo, no en el modelo mismo. En TikTok, reemplazar unos 800 modelos de percepción especializados por un solo LLM multimodal solo funcionó cuando la cuantización INT8 y el servicio tensor-paralelo en Inf2 redujeron el costo de inferencia a la mitad. En AiFi usábamos solo cámaras, sin sensores de peso en las estanterías, lo que abarataba el hardware pero ponía toda la presión sobre la detección y el seguimiento. Me muevo en toda la pila: investigación, entrenamiento e inferencia a gran escala, despliegue con Docker, Kubernetes, TensorRT y edge, y las capas de servicio y producto encima. También reviso para CVPR, ECCV, ICCV e IEEE TPAMI."
  },
  projects: {
    title: "Investigación y Proyectos",
    data: [
      {
        title: "Controlador PID para Deep Learning",
        description:
            "SGD con momentum es básicamente un controlador PI: usa el gradiente actual y el pasado acumulado, pero sin término derivativo. Agregar la D deja que el optimizador anticipe el sobreimpulso, y eso hizo el entrenamiento más rápido y estable. CVPR 2018 Spotlight, extendido a IEEE TNNLS (IF 11.368).",
        tags: ["CVPR Spotlight", "Optimization", "PyTorch"],
        imageUrl: pidImg,
        imageFit: "contain" as const,
        imageBg: "oklch(23% 0.025 250)",
        repositoryUrl: "https://openaccess.thecvf.com/content_cvpr_2018/html/An_A_PID_Controller_CVPR_2018_paper.html",
        repoType: "CVPR Page",
        demoType: "Paper",
        demoUrl: "https://arxiv.org/abs/1808.03337",
      },
      {
        title: "Asistente de Compras Caper Cart",
        description:
          "En Instacart: un VLM que corre en el Caper Cart y confirma cada producto cuando el cliente lo escanea. Alcanza un 95% de exactitud top-1 en más de 300K SKUs, y la infraestructura de servicio y evaluación que construí bajó las identificaciones erróneas un 30%. El carrito tiene presupuestos fijos de latencia, consumo y costo, así que buena parte del trabajo es decidir qué corre en el dispositivo y qué en la nube. Soy responsable de extremo a extremo, del modelo al producto.",
        tags: ["VLM", "Multimodal", "Edge Inference", "Full Stack"],
        imageUrl: caperImg,
        imageFit: "contain" as const,
        imageBg: "oklch(97% 0.03 145)",
        repositoryUrl: "",
        repoType: "",
        demoType: "Industry",
        demoUrl: "#",
      },
      {
        title: "LLM Multimodal a Escala",
        description:
          "En TikTok: un LLM multimodal que convierte video en texto denso y reemplaza unos 800 modelos de percepción especializados. Reemplazar tantos modelos solo vale la pena si ese único modelo sale más barato que todos juntos, así que la mayor parte del esfuerzo fue la cuantización INT8 y el servicio tensor-paralelo en Inf2. Eso redujo el costo de inferencia a la mitad y lo hizo viable con 300 millones de videos al día. Después se expandió a recomendación, publicidad y búsqueda (AWS re:Invent 2024).",
        tags: ["LLM", "Multimodal", "Inf2", "re:Invent 2024"],
        imageUrl: tiktokImg,
        imageFit: "contain" as const,
        imageBg: "oklch(12% 0.005 0)",
        repositoryUrl: "https://www.youtube.com/watch?v=cdrZHimJY2k&t=2186",
        repoType: "re:Invent Talk",
        demoType: "AWS Blog",
        demoUrl: "https://aws.amazon.com/cn/blogs/machine-learning/bytedance-processes-billions-of-daily-videos-using-their-multimodal-video-understanding-models-on-aws-inferentia2/",
      },
      {
        title: "Sistema de Pago Autónomo",
        description:
          "En AiFi: pago autónomo solo con cámaras, parecido a Amazon Go pero sin sensores de peso en las estanterías. Usar solo cámaras abarata la tienda, pero obliga al sistema de visión a hacerlo todo: detección y seguimiento de personas y productos que aguanten oclusión y multitudes con la precisión suficiente para cobrarle al carrito correcto. Lideré esos algoritmos. Patente US11393213B2.",
        tags: ["Object Detection", "Tracking", "Patent"],
        imageUrl: aifiImg,
        imageFit: "contain" as const,
        imageBg: "oklch(94% 0.04 210)",
        repositoryUrl: "",
        repoType: "",
        demoType: "Patent",
        demoUrl: "https://patents.google.com/patent/US11393213B2",
      },
      {
        title: "Sistema PolyU-Face",
        description:
          "En HK PolyU: un pipeline facial completo, con detección, alineación y reconocimiento. Armar toda la cadena deja claro algo que los papers suelen pasar por alto: los errores de alineación al principio limitan la precisión final mucho más que el propio modelo de reconocimiento. También contribuí a la estimación de pose 2D multipersona (ICME 2018 oral).",
        tags: ["Face Recognition", "Pose Estimation", "Research"],
        imageUrl: polyuImg,
        imageFit: "contain" as const,
        imageBg: "oklch(18% 0.025 27)",
        repositoryUrl: "",
        repoType: "",
        demoType: "Research",
        demoUrl: "#",
      },
    ],
  },
  skills: {
    title: "Mis Habilidades",
    data: [
      // Lenguajes de Programación
      {
        skill: "Python",
        icon: React.createElement(SiPython),
        color: "#3572A5",
      },
      {
        skill: "C++",
        icon: React.createElement(SiCplusplus),
        color: "#00599C",
      },
      // Frameworks ML
      {
        skill: "PyTorch",
        icon: React.createElement(SiPytorch),
        color: "#EE4C2C",
      },
      {
        skill: "TensorFlow",
        icon: React.createElement(SiTensorflow),
        color: "#FF6F00",
      },
      {
        skill: "Keras",
        icon: React.createElement(SiKeras),
        color: "#D00000",
      },
      {
        skill: "ONNX",
        icon: React.createElement(SiOnnx),
        color: "#005CED",
      },
      {
        skill: "TensorRT",
        icon: React.createElement(SiNvidia),
        color: "#76B900",
      },
      {
        skill: "OpenCV",
        icon: React.createElement(SiOpencv),
        color: "#5C3EE8",
      },
      // Despliegue
      {
        skill: "Docker",
        icon: React.createElement(SiDocker),
        color: "#2496ED",
      },
      {
        skill: "Kubernetes",
        icon: React.createElement(SiKubernetes),
        color: "#326CE5",
      },
      // Desarrollo Web
      {
        skill: "TypeScript",
        icon: React.createElement(SiTypescript),
        color: "#3178c6",
      },
      {
        skill: "React",
        icon: React.createElement(SiReact),
        color: "#61dafb",
      },
      {
        skill: "Next.js",
        icon: React.createElement(SiNextdotjs),
        color: "#000000",
      },
      {
        skill: "Tailwind",
        icon: React.createElement(SiTailwindcss),
        color: "#38bdf8",
      },
      // Herramientas
      {
        skill: "Git",
        icon: React.createElement(SiGit),
        color: "#f05032",
      },
      {
        skill: "PostgreSQL",
        icon: React.createElement(SiPostgresql),
        color: "#336791",
      },
    ],
  },
  experience: {
    title: "Mi Experiencia",
    data: [
      {
        title: "Instacart Inc. - Ingeniero Staff de ML Full Stack",
        location: "California, EE.UU.",
        description:
          "Construí un Vision Language Model sobre el Caper Cart que confirma los productos cuando el cliente los escanea: 95% de exactitud top-1 en más de 300K SKUs, con un 30% menos de identificaciones erróneas. Soy responsable del sistema de extremo a extremo: modelo, inferencia y la experiencia de producto en el carrito.",
        icon: React.createElement(LuBriefcase),
        date: "Abril 2025 - Presente",
      },
      {
        title: "TikTok Inc. - Ingeniero ML Senior",
        location: "San José, California, EE.UU.",
        description:
          "Confianza y Seguridad y Publicidad de Contenido: Construí un sistema LLM multimodal completo que convierte videos en texto denso y detecta contenido no seguro, procesando 300 millones de videos al día en Inf2 y superando a 800 modelos de percepción tradicionales. Expandí el LLM multimodal a recomendación, publicidad y búsqueda (presentado en AWS re:Invent 2024), y subí las métricas de moderación de audio del 5% al 10% integrando Whisper.",
        icon: React.createElement(LuBriefcase),
        date: "Septiembre 2021 - Abril 2025",
      },
      {
        title: "Meta (Facebook) - Ingeniero de CV en FAIR",
        location: "Menlo Park, California, EE.UU.",
        description:
          "Facebook AI Research: Trabajé en IA Explicable para abrir la 'caja negra' de los modelos de visión por computadora, y en IA Responsable para la inclusión y equidad en sistemas de Visión por Computadora.",
        icon: React.createElement(LuBriefcase),
        date: "Diciembre 2020 - Agosto 2021",
      },
      {
        title: "AiFi Inc. - Ingeniero de Investigación Senior",
        location: "Santa Clara, California, EE.UU.",
        description:
          "Lideré los algoritmos de detección de visión por computadora para una solución de pago autónomo solo con RGB (similar a Amazon Go). Desarrollé sistemas de detección y seguimiento de clientes y productos. Titular de patente estadounidense.",
        icon: React.createElement(LuBriefcase),
        date: "Junio 2018 - Noviembre 2020",
      },
      {
        title: "Universidad Politécnica de HK - Asistente de Investigación",
        location: "Hong Kong",
        description:
          "Desarrollé el optimizador PID para acelerar el entrenamiento de CNN (CVPR 2018 Spotlight). Construí el sistema PolyU-Face para detección, alineación y reconocimiento facial.",
        icon: React.createElement(LuBuilding2),
        date: "Octubre 2016 - Abril 2018",
      },
      {
        title: "Universidad Tsinghua - Maestría",
        location: "Pekín, China",
        description:
          "Ingeniería de Control con investigación en optimización de deep learning, estimación de pose humana, detección de objetos y reconocimiento facial. Instructor asistente para el curso de Big Data.",
        icon: React.createElement(LuGraduationCap),
        date: "Agosto 2015 - Julio 2018",
      },
      {
        title: "Taiyuan Iron and Steel Corp. - Técnico de Control",
        location: "Taiyuan, China",
        description:
          "Especializado en ajuste de parámetros de controladores PID en laminadoras de acero. Aplicación de teoría de control a la automatización industrial.",
        icon: React.createElement(LuBriefcase),
        date: "Julio 2012 - Septiembre 2014",
      },
      {
        title: "Universidad de Ciencia y Tecnología de Kunming - Grado",
        location: "Kunming, China",
        description:
          "Ciencia de Instrumentación y Medición con concentración en teoría de control. Fundamentos en sistemas de control PID y procesamiento de señales.",
        icon: React.createElement(LuGraduationCap),
        date: "Septiembre 2008 - Julio 2012",
      },
    ],
  },
  contact: {
    title: "Contáctame",
    message:
      "No dudes en escribirme, sobre todo si es por nuevas oportunidades. Puedes usar el formulario de abajo, enviarme un correo directo o encontrarme en LinkedIn o GitHub. Te responderé.",
    placeholderEmail: "Tu correo electrónico",
    placeholderMessage: "Tu mensaje",
    buttonSubmit: "Enviar",
    buttonCopy: "Copiar dirección de correo",
    toastSent: "¡Correo enviado con éxito!",
    toastCopied: "¡Dirección de correo copiada al portapapeles!",
  },
  footer: {
    text: "Todos los derechos reservados.",
    subtitle: "Acerca de este sitio",
    about:
      "construido con React, Next.js, TypeScript, Tailwind CSS, Framer Motion, alojado en Vercel.",
  },
};

export default es;
